"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Wrench } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const brands = [{
  id: "rheem",
  name: "Rheem",
  category: "Tank & Tankless Systems",
  description: "Full diagnostics, warranty parts, and emergency replacement.",
  tag: "Certified Partner"
}, {
  id: "bradford-white",
  name: "Bradford White",
  category: "Commercial & Residential",
  description: "Professional-grade tank servicing and rapid leak mitigation.",
  tag: "Authorized Service"
}, {
  id: "ao-smith",
  name: "A.O. Smith",
  category: "High-Efficiency Water Heaters",
  description: "Gas, electric, and hybrid heat pump emergency repairs.",
  tag: "Factory Trained"
}, {
  id: "navien",
  name: "Navien",
  category: "Condensing Tankless",
  description: "Descaling, error-code clearing, and continuous flow maintenance.",
  tag: "Tankless Specialists"
}, {
  id: "rinnai",
  name: "Rinnai",
  category: "Tankless & Commercial",
  description: "Precision burner servicing and instant heat unit restoration.",
  tag: "Preferred Techs"
}];
export default function BrandRibbon() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="brandsweserviceribbon" data-nav="light" className="w-full bg-background text-foreground py-12 md:py-16 border-y border-border/70 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Ribbon */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-border/50">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <ShieldCheck className="size-3.5 text-accent" />
                Authorized Repair & Replacement
              </Badge>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
              Major Brands We Service in Jonesboro
            </h2>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground font-sans">
              Our 24/7 emergency plumbers carry authentic factory replacement parts and specialized diagnostic tools for leading plumbing and water heater systems.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline" className="font-semibold text-foreground border-border hover:bg-secondary/60">
              <Link to="/brands/" className="inline-flex items-center gap-2">
                <span>View All Brands</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Brand Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-8">
          {brands.map((brand, i) => <motion.div key={brand.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-xs hover:shadow-md flex flex-col justify-between p-5">
                <CardContent className="p-0 flex flex-col justify-between h-full space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="inline-flex items-center justify-center size-8 rounded-md bg-secondary text-primary font-bold">
                        <Wrench className="size-4 text-primary" />
                      </span>
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {brand.tag}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-card-foreground font-default tracking-tight">
                      {brand.name}
                    </h3>
                    <p className="text-xs font-medium text-primary/80 mt-0.5">
                      {brand.category}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      {brand.description}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-border/40 flex items-center gap-1.5 text-xs text-foreground/80 font-medium">
                    <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                    <span>24/7 Service Available</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Quick Micro-Banner */}
        <div className="mt-8 rounded-lg bg-secondary/40 border border-border/60 p-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-xs sm:text-sm text-foreground/90 font-medium">
            Have a different water heater or fixture brand? We service all standard residential and commercial plumbing systems.
          </p>
          <Button asChild size="sm" variant="default" className="shrink-0 font-medium shadow-xs">
            <Link to="/contact/">Request Brand Service</Link>
          </Button>
        </div>
      </div>
    </section>;
}